import { graphql } from '@frontend/gql/social';

export const socialAdsAnalyticsFragment = graphql(`
  fragment SocialAdsAnalytics_Query on Query {
    client(id: $clientId) {
      id
      metaAdAccounts {
        id
        name
        createdAt
        updatedAt
        status
      }
      # New: sumInsightsBy for top ads data (logging only for now)
      sumInsightsBy(filter: { range: $currentIntervalRange, isAspireSourced: true }, groupBy: { type: AD }) {
        by {
          __typename
          ... on MetaAd {
            id
            name
            metaAdMetaAdCreatives {
              instagramActorId
              socialAccount {
                username
              }
            }
          }
        }
        metrics {
          roas
          impressions
          reach
          clicks
          spend {
            amount
            currency
          }
          purchase {
            amount
            currency
          }
          cpm
          ctr
          cpc
          cpp
          creators
          ads
          posts
        }
      }
      # New: sumInsightsBy for top social accounts data
      sumInsightsBySocialAccounts: sumInsightsBy(
        filter: { range: $currentIntervalRange, isAspireSourced: true }
        groupBy: { type: SOCIAL_ACCOUNT }
      ) {
        by {
          __typename
          ... on MetaSocialAccount {
            externalId
            username
            fullName
            profilePictureUrl
            bio
            website
            verifiedUser
          }
        }
        metrics {
          roas
          impressions
          reach
          clicks
          spend {
            amount
            currency
          }
          purchase {
            amount
            currency
          }
          cpm
          ctr
          cpc
          cpp
          creators
          ads
          posts
        }
      }
      currentInterval: sumInsights(
        filter: { range: $currentIntervalRange, isAspireSourced: true }
        fromOldData: $fromOldData
      ) {
        ...TotalAdsCard_MetaSumInsights
        ...TotalSpendCard_MetaSumInsights
        ...SalesGeneratedCard_MetaSumInsights
        ...RoasCard_MetaSumInsights
        ...Clicks_MetaSumInsights
        ...Reach_MetaSumInsights
        ...Impressions_MetaSumInsights
        ...Members_MetaSumInsights
        ...Posts_MetaSumInsights
        ...Cpc_MetaSumInsights
        ...Cpm_MetaSumInsights
        ...Cpp_MetaSumInsights
        ...Ctr_MetaSumInsights
        ...CpcRadial_MetaSumInsights
        ...CpmRadial_MetaSumInsights
        ...CppRadial_MetaSumInsights
        ...CtrRadial_MetaSumInsights
        ...SocialAds_CurrencyFragment
      }
      currentIntervalsIntervalInsights: intervalInsights(
        filter: { range: $currentIntervalRange, isAspireSourced: true }
        fromOldData: $fromOldData
      ) {
        ...AdSpendVsSalesChart_MetaIntervalInsights
        ...RoasOverTimeChart_MetaInsights
        ...ImpressionsChart_MetaInsights
        ...ReachChart_MetaInsights
        ...ViewsPerPersonChart_MetaInsights
      }
      currentIntervalsIntervalInsightsNotAspireSourced: intervalInsights(
        filter: { range: $currentIntervalRange, isAspireSourced: false }
        fromOldData: $fromOldData
      ) {
        ...AdSpendVsSalesChart_MetaIntervalInsights
        ...RoasOverTimeChart_MetaInsights
        ...ImpressionsChart_MetaInsights
        ...ReachChart_MetaInsights
        ...ViewsPerPersonChart_MetaInsights
      }
      previousInterval: sumInsights(
        filter: { range: $previousIntervalRange, isAspireSourced: true }
        fromOldData: $fromOldData
      ) {
        ...TotalAdsCard_MetaSumInsights
        ...TotalSpendCard_MetaSumInsights
        ...SalesGeneratedCard_MetaSumInsights
        ...RoasCard_MetaSumInsights
        ...Clicks_MetaSumInsights
        ...Reach_MetaSumInsights
        ...Impressions_MetaSumInsights
        ...Members_MetaSumInsights
        ...Posts_MetaSumInsights
        ...Cpc_MetaSumInsights
        ...Cpm_MetaSumInsights
        ...Cpp_MetaSumInsights
        ...Ctr_MetaSumInsights
      }
      currentIntervalNotAspireSourced: sumInsights(
        filter: { range: $currentIntervalRange, isAspireSourced: false }
        fromOldData: $fromOldData
      ) {
        ...TotalAdsCard_MetaSumInsights
        ...TotalSpendCard_MetaSumInsights
        ...SalesGeneratedCard_MetaSumInsights
        ...RoasCard_MetaSumInsights
        ...Cpc_MetaSumInsights
        ...Cpm_MetaSumInsights
        ...Cpp_MetaSumInsights
        ...Ctr_MetaSumInsights
        ...CpcRadial_MetaSumInsights
        ...CpmRadial_MetaSumInsights
        ...CppRadial_MetaSumInsights
        ...CtrRadial_MetaSumInsights
      }
      previousIntervalNotAspireSourced: sumInsights(
        filter: { range: $previousIntervalRange, isAspireSourced: false }
        fromOldData: $fromOldData
      ) {
        ...TotalAdsCard_MetaSumInsights
        ...TotalSpendCard_MetaSumInsights
        ...SalesGeneratedCard_MetaSumInsights
        ...RoasCard_MetaSumInsights
        ...Cpc_MetaSumInsights
        ...Cpm_MetaSumInsights
        ...Cpp_MetaSumInsights
        ...Ctr_MetaSumInsights
      }
    }
  }
`);

export const insightsByClientId = graphql(`
  query GetInsightsByClientId(
    $clientId: String!
    $currentIntervalRange: RangeInput!
    $previousIntervalRange: RangeInput!
    $fromOldData: Boolean
  ) {
    ...SocialAdsAnalytics_Query
  }
`);

export const getMetaAdAccountsForFilters = graphql(`
  query GetMetaAdAccountsForFilters(
    $clientId: String!
    $accountStatuses: [MetaAdAccountStatus!] = [ACTIVE, ANY_ACTIVE]
    $campaignStatuses: [MetaAdCampaignEffectiveStatus!]
    $adSetStatuses: [MetaAdSetEffectiveStatus!]
    $adStatuses: [MetaAdEffectiveStatus!]
    $includeAccountFields: Boolean = true
    $includeCampaignFields: Boolean = true
    $includeAdSetFields: Boolean = true
    $includeAdFields: Boolean = true
    $includeCreativeFields: Boolean = true
  ) {
    client(id: $clientId) {
      id
      metaAdAccounts(filter: { statuses: $accountStatuses }) {
        id
        name @include(if: $includeAccountFields)
        metaAdCampaigns(filter: { statuses: $campaignStatuses }) @include(if: $includeCampaignFields) {
          id
          name
          objective
          metaAdSets(filter: { statuses: $adSetStatuses }) @include(if: $includeAdSetFields) {
            id
            name
            metaAds(filter: { statuses: $adStatuses }) @include(if: $includeAdFields) {
              id
              metaAdMetaAdCreatives @include(if: $includeCreativeFields) {
                instagramBrandedContent
              }
            }
          }
        }
      }
    }
  }
`);

export const socialAdsAnalyticsTopAdsFragment = graphql(`
  fragment SocialAdsAnalyticsTopAds_Query on Query {
    client(id: $clientId) {
      id
      metaAdAccounts {
        id
        name
        createdAt
        updatedAt
        status
      }
      # New: sumInsightsBy for top ads data (logging only for now)
      sumInsightsBy(filter: { range: $currentIntervalRange, isAspireSourced: true }, groupBy: { type: AD }) {
        by {
          __typename
          ... on MetaAd {
            id
            name
            metaAdMetaAdCreatives {
              instagramActorId
              socialAccount {
                username
              }
            }
          }
        }
        metrics {
          roas
          impressions
          reach
          clicks
          spend {
            amount
            currency
          }
          purchase {
            amount
            currency
          }
          cpm
          ctr
          cpc
          cpp
          creators
          ads
          posts
        }
      }
    }
  }
`);

export const socialAdsAnalyticsTopSocialAccountsFragment = graphql(`
  fragment SocialAdsAnalyticsTopSocialAccounts_Query on Query {
    client(id: $clientId) {
      id
      sumInsightsBySocialAccounts: sumInsightsBy(
        filter: { range: $currentIntervalRange, isAspireSourced: true }
        groupBy: { type: SOCIAL_ACCOUNT }
      ) {
        by {
          __typename
          ... on MetaSocialAccount {
            externalId
            username
            fullName
            profilePictureUrl
            bio
            website
            verifiedUser
          }
        }
        metrics {
          roas
          impressions
          reach
          clicks
          spend {
            amount
            currency
          }
          purchase {
            amount
            currency
          }
          cpm
          ctr
          cpc
          cpp
          creators
          ads
          posts
        }
      }
    }
  }
`);

export const getTopAdsSumInsights = graphql(`
  query GetTopAdsSumInsights($clientId: String!, $currentIntervalRange: RangeInput!) {
    ...SocialAdsAnalyticsTopAds_Query
  }
`);

export const getTopSocialAccountsSumInsights = graphql(`
  query GetTopSocialAccountsSumInsights($clientId: String!, $currentIntervalRange: RangeInput!) {
    ...SocialAdsAnalyticsTopSocialAccounts_Query
  }
`);
